import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { 
  User, Mail, Phone, MapPin, Building2, Save, Loader2, 
  CheckCircle2, AlertCircle, Globe, Linkedin, Shield
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

const TIPOS_DOADOR = ['Individual', 'Empresa', 'Fundação', 'Associação', 'Instituição Pública'];
const TIPOS_AJUDA = ['Produto', 'Financeira', 'Voluntariado', 'Equipamento', 'Serviço'];

export default function DoadorPerfilPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [distritos, setDistritos] = useState([]);
  const [nifValidation, setNifValidation] = useState(null);

  useEffect(() => {
    loadPerfil();
    loadDistritos();
  }, []);

  const loadPerfil = async () => {
    try {
      const data = await doadorApiCall('/doador/perfil');
      setPerfil(data.doador || {});
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const loadDistritos = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`);
      if (res.ok) {
        const data = await res.json();
        setDistritos(data || []);
      }
    } catch (e) {
      console.error('Erro ao carregar distritos:', e);
    }
  };

  const validarNif = async (nif) => {
    if (!nif || nif.length < 9) {
      setNifValidation(null);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doador/validar-nif`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nif })
      });
      const data = await res.json();
      setNifValidation(data);
    } catch (e) {
      setNifValidation({ valido: false, mensagem: 'Erro ao validar' });
    }
  };

  const handleChange = (field, value) => {
    setPerfil(prev => ({ ...prev, [field]: value }));
    if (field === 'nif') {
      validarNif(value);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await doadorApiCall('/doador/perfil', {
        method: 'PUT',
        body: JSON.stringify(perfil)
      });
      
      if (result.sucesso) {
        toast.success('Perfil atualizado com sucesso!');
      } else {
        toast.error(result.erro || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao guardar perfil');
    } finally {
      setSaving(false);
    }
  };

  const toggleInteresse = (tipo) => {
    const interesses = perfil.tipos_recursos_interesse || [];
    if (interesses.includes(tipo)) {
      handleChange('tipos_recursos_interesse', interesses.filter(t => t !== tipo));
    } else {
      handleChange('tipos_recursos_interesse', [...interesses, tipo]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doador-perfil-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <User className="w-7 h-7 text-rose-500" />
            Meu Perfil
          </h1>
          <p className="text-slate-500 mt-1">Mantenha os seus dados atualizados</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Guardar Alterações</>
          )}
        </Button>
      </div>

      {/* Dados Pessoais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-slate-500" />
            Dados Pessoais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={perfil.nome || ''}
                onChange={(e) => handleChange('nome', e.target.value)}
                placeholder="Seu nome"
              />
            </div>
            <div>
              <Label>Tipo de Doador</Label>
              <Select 
                value={perfil.tipo_doador || 'Individual'} 
                onValueChange={(v) => handleChange('tipo_doador', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOADOR.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {perfil.tipo_doador !== 'Individual' && (
            <div>
              <Label>Nome Comercial / Empresa</Label>
              <Input
                value={perfil.nome_comercial || ''}
                onChange={(e) => handleChange('nome_comercial', e.target.value)}
                placeholder="Nome da empresa ou organização"
              />
            </div>
          )}

          {/* NIF */}
          <div>
            <Label>NIF {perfil.tipo_doador !== 'Individual' && '*'}</Label>
            <div className="relative">
              <Input
                value={perfil.nif || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                  handleChange('nif', value);
                }}
                placeholder="123456789"
                maxLength={9}
                className={nifValidation ? (nifValidation.valido ? 'border-green-500' : 'border-red-500') : ''}
              />
              {nifValidation && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {nifValidation.valido ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {nifValidation && (
              <p className={`text-xs mt-1 ${nifValidation.valido ? 'text-green-600' : 'text-red-600'}`}>
                {nifValidation.mensagem} {nifValidation.tipo && `(${nifValidation.tipo})`}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contactos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-500" />
            Contactos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Telefone</Label>
              <Input
                value={perfil.telefone || ''}
                onChange={(e) => handleChange('telefone', e.target.value)}
                placeholder="+351 XXX XXX XXX"
              />
            </div>
            <div>
              <Label>Telemóvel</Label>
              <Input
                value={perfil.telemovel || ''}
                onChange={(e) => handleChange('telemovel', e.target.value)}
                placeholder="+351 9XX XXX XXX"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={perfil.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://..."
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>LinkedIn</Label>
              <div className="relative">
                <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={perfil.linkedin || ''}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  placeholder="linkedin.com/in/..."
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Morada */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-500" />
            Morada
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Morada</Label>
            <Input
              value={perfil.morada || ''}
              onChange={(e) => handleChange('morada', e.target.value)}
              placeholder="Rua, número, andar..."
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Código Postal</Label>
              <Input
                value={perfil.codigo_postal || ''}
                onChange={(e) => handleChange('codigo_postal', e.target.value)}
                placeholder="0000-000"
              />
            </div>
            <div>
              <Label>Distrito</Label>
              <Select 
                value={perfil.distrito_id || ''} 
                onValueChange={(v) => {
                  const distrito = distritos.find(d => d.id === v);
                  handleChange('distrito_id', v);
                  handleChange('distrito_nome', distrito?.nome || '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {distritos.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Concelho</Label>
              <Input
                value={perfil.concelho || ''}
                onChange={(e) => handleChange('concelho', e.target.value)}
                placeholder="Concelho"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferências */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-500" />
            Preferências
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-3 block">Áreas de Interesse</Label>
            <div className="flex flex-wrap gap-2">
              {TIPOS_AJUDA.map(tipo => (
                <Badge
                  key={tipo}
                  variant={perfil.tipos_recursos_interesse?.includes(tipo) ? 'default' : 'outline'}
                  className={`cursor-pointer ${
                    perfil.tipos_recursos_interesse?.includes(tipo) 
                      ? 'bg-rose-500 hover:bg-rose-600' 
                      : 'hover:bg-rose-50'
                  }`}
                  onClick={() => toggleInteresse(tipo)}
                >
                  {tipo}
                </Badge>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newsletter"
                checked={perfil.newsletter || false}
                onCheckedChange={(v) => handleChange('newsletter', v)}
              />
              <Label htmlFor="newsletter" className="font-normal">
                Quero receber newsletter com novidades e campanhas
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="contacto"
                checked={perfil.aceita_ser_contactado !== false}
                onCheckedChange={(v) => handleChange('aceita_ser_contactado', v)}
              />
              <Label htmlFor="contacto" className="font-normal">
                Aceito ser contactado para oportunidades de doação
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info RGPD */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Proteção de Dados (RGPD)</p>
              <p className="mt-1">
                Os seus dados são tratados de acordo com o Regulamento Geral de Proteção de Dados. 
                Pode exercer os seus direitos contactando-nos através de rgpd@entrajuda.pt
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
